import axios from 'helpers/api';
import { useEffect, useState } from "react";
import { Table, Button, Badge, Modal, Spinner, Alert, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDesktop, faMobileAlt, faTabletAlt, faTerminal, faGlobe, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { toast } from "react-toastify";

const getDeviceInfo = (loginSource, userAgent) => {
    const ua = userAgent?.toLowerCase() || "";
    if (ua.includes("postman")) return { icon: faTerminal, label: "Postman" };
    if (ua.includes("mobile") || ua.includes("android") || ua.includes("iphone"))
        return { icon: faMobileAlt, label: "Mobile" };
    if (ua.includes("tablet") || ua.includes("ipad"))
        return { icon: faTabletAlt, label: "Tablet" };
    switch (loginSource) {
        case 1: return { icon: faDesktop, label: "Web Browser" };
        case 2: return { icon: faMobileAlt, label: "Mobile App" };
        default: return { icon: faGlobe, label: "Unknown" };
    }
};

const UserActiveSessionsTab = ({userId}) => {
    const [userData, setUserData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [terminatingId, setTerminatingId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [terminatingAll, setTerminatingAll] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            const { data } = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/users/active-sessions`,
                { headers: { Authorization: `Bearer ${tokenStr}` }, params: { user_id: userId } }
            );
            setUserData(data.data);
            setCurrentPage(1);
        } catch {
            toast.error("Failed to fetch active sessions.");
        } finally {
            setLoading(false);
        }
    };

    const handleTerminate = async (session) => {
        const confirmed = window.confirm(`Terminate session at ${session.ip_address}?`);
        if (!confirmed) return;
        setTerminatingId(session.session_id);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/users/terminate-session`,
                { session_id: session.session_id },
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );
            toast.success(`Session at ${session.ip_address} terminated.`);
            fetchData();
        } catch {
            toast.error("Failed to terminate session.");
        } finally {
            setTerminatingId(null);
        }
    };

    const handleTerminateAll = async () => {
        setTerminatingAll(true);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/users/terminate-all-sessions`,
                { user_id: userId },
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );
            setShowModal(false);
            toast.success(`${data.terminated_count ?? "All other"} session(s) terminated successfully.`);
            fetchData();
        } catch {
            toast.error("Failed to terminate all sessions.");
        } finally {
            setTerminatingAll(false);
        }
    };

    const handleDisableAndTerminateAll = async () => {
        setTerminatingAll(true);
        try {
            let tokenStr = localStorage.getItem("accessToken");
            const { data } = await axios.post(
                `${process.env.REACT_APP_SERVER_URL}/users/force-disable-user`,
                { user_id: userId },
                { headers: { Authorization: `Bearer ${tokenStr}` } }
            );
            setShowDisableModal(false);
            toast.success(data.message);
            fetchData();
        } catch {
            toast.error("Failed to disable and terminate all sessions.");
        } finally {
            setTerminatingAll(false);
        }
    };

    const totalPages = Math.ceil(userData.length / rowsPerPage);
    const paginatedData = userData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );
    const otherSessionsCount = userData.filter(s => !s.is_current_session).length;

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                {otherSessionsCount > 0 && (
                    <Button variant="danger" size="sm" onClick={() => setShowModal(true)}>
                        <FontAwesomeIcon icon={faGlobe} className="me-2" />
                        Terminate All Sessions
                        <Badge bg="light" text="danger" className="ms-2">{otherSessionsCount}</Badge>
                    </Button>
                )}
                {(
                    <Button variant="danger" size="sm" onClick={() => setShowDisableModal(true)}>
                        <FontAwesomeIcon icon={faGlobe} className="me-2" />
                        Disable User
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : userData.length === 0 ? (
                <Alert variant="info" className="text-center">No active sessions found.</Alert>
            ) : (
                <>
                    <Table striped bordered hover responsive size="sm" className="fs-10 mb-2">
                        <thead className="bg-200 text-900">
                            <tr>
                                <th>Device</th>
                                <th>IP Address</th>
                                <th>Login Time</th>
                                <th>Last Activity</th>
                                <th>Status</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((session) => {
                                const { icon, label } = getDeviceInfo(session.login_source, session.user_agent);
                                return (
                                    <tr key={session.session_id} className={session.is_current_session ? "table-success" : ""}>
                                        <td>
                                            <FontAwesomeIcon icon={icon} className="me-2 text-primary" />
                                            {label}
                                            {session.is_current_session && (
                                                <Badge bg="success" className="ms-2">Current</Badge>
                                            )}
                                        </td>
                                        <td>{session.ip_address}</td>
                                        <td>{moment(session.login_time).format("MM/DD/YY hh:mm A")}</td>
                                        <td>
                                            {session.last_activity_at
                                                ? moment(session.last_activity_at).fromNow()
                                                : <span className="text-muted">—</span>}
                                        </td>
                                        <td>
                                            <Badge bg={session.is_socket_connected ? "success" : "secondary"}>
                                                {session.is_socket_connected ? "Online" : "Offline"}
                                            </Badge>
                                        </td>
                                        <td className="text-center">
                                            {!session.is_current_session && (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    disabled={terminatingId === session.session_id}
                                                    onClick={() => handleTerminate(session)}
                                                >
                                                    {terminatingId === session.session_id
                                                        ? <Spinner animation="border" size="sm" />
                                                        : "Terminate"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>

                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted fs-11">Rows per page:</span>
                            <Form.Select
                                size="sm"
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                                style={{ width: '70px' }}
                            >
                                {[5, 10, 25, 50].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </Form.Select>
                        </div>
                        <span className="text-muted fs-11">
                            {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, userData.length)} of {userData.length} sessions
                        </span>
                        <div className="d-flex align-items-center gap-1">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                            >«</Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                            </Button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${idx}`} className="px-1 text-muted">…</span>
                                    ) : (
                                        <Button
                                            key={p}
                                            size="sm"
                                            variant={currentPage === p ? "primary" : "outline-secondary"}
                                            onClick={() => setCurrentPage(p)}
                                        >
                                            {p}
                                        </Button>
                                    )
                                )
                            }

                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                <FontAwesomeIcon icon={faChevronRight} />
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(totalPages)}
                            >»</Button>
                        </div>
                    </div>
                </>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">Terminate All Other Sessions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to terminate{" "}
                    <strong>all {otherSessionsCount} other session(s)</strong>?
                    Your current session will remain active.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button variant="danger" size="sm" disabled={terminatingAll} onClick={handleTerminateAll}>
                        {terminatingAll ? (
                            <><Spinner animation="border" size="sm" className="me-2" />Terminating...</>
                        ) : "Yes, Terminate All"}
                    </Button>
                </Modal.Footer>
            </Modal>
             <Modal show={showDisableModal} onHide={() => setShowDisableModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">Disable User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    This will deactivate the user and terminate all their active sessions. This action cannot be undone from here.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowDisableModal(false)}>Cancel</Button>
                    <Button variant="danger" size="sm" disabled={terminatingAll} onClick={handleDisableAndTerminateAll}>
                        {terminatingAll ? (
                            <><Spinner animation="border" size="sm" className="me-2" />Terminating...</>
                        ) : "Yes, Terminate All"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default UserActiveSessionsTab;
